import React, { useCallback, useEffect, useMemo } from "react";
import SearchInput from "~/app/modules/form/Search";
import MForm from "~/app/modules/form";
import Select from "~/app/modules/form/Select";
import Textarea from "~/app/modules/form/Textarea";
import Text from "~/app/modules/form/Text";
import useForm from "~/app/modules/form/useForm";
import useTranslation from "~/app/hooks/useTranslation";
import { API } from "~/app/routes";
import { Machine, MachineSearch } from "~/types";
import { useStoreActions, useStoreState } from "~/store";
import { CreateWorkOrderFormValues } from "~/workorder/types";
import classes from "./style.module.scss";
import Loader from "~/app/common/Loader";
import { notify } from "~/app/helpers/notify";
import { formatMachineSearchLabel, formatMachineSearchValue } from "~/machines/helpers";

export type MainFormProps = {
  machineId?: Machine["id"];
};

export type MainFormComponent = React.FC<MainFormProps>;

const MainForm: MainFormComponent = ({ machineId }) => {

  const { t } = useTranslation("workorder.create");
  const { t: tWorkorder } = useTranslation("workorder");
  const { values, fieldEvents, setValue } = useForm<CreateWorkOrderFormValues>({
    formName: "createWorkOrder",
  });
  const { id, intern, name } = useStoreState((state) => state.machine.data);
  const fetching = useStoreState((state) => state.machine.fetching);
  const fetchMachine = useStoreActions((actions) => actions.machine.fetchMachine);
  const getWorkorderDepartments = useStoreActions((actions) => actions.workOrder.getWorkorderDepartments);
  const departments = useStoreState((state) => state.workOrder.departments ?? []);
  const fetchingDepartments = useStoreState((state) => state.workOrder.fetchingDepartments);

  const departmentsOptions = useMemo(() => departments.map((department) => ({
    text: department.name,
    value: department.id,
  })), [departments]);

  const machineDisplay = useMemo(() => {
    if (machineId && id) {
      return `${intern} – ${name}`;
    }
    return "";
  }, [id, intern, name, machineId]);

  useEffect(() => {
    if (!machineId) {
      return;
    }
    setValue("machineId", machineId);
    if (String(id) !== String(machineId)) {
      fetchMachine(machineId);
    }
  }, [machineId, id, setValue, fetchMachine]);

  useEffect(() => {
    getWorkorderDepartments();
  }, [getWorkorderDepartments]);

  const handleDepartmentChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const departmentId = Number(e.target.value);
      const department = departments.find(department => department.id === departmentId);
      if (!department) {
        return;
      }
      if (!department.hasCostCategory) {
        notify(tWorkorder("error.departmentNoCostCategory"), "error");
        return;
      }
      if (department.users.length === 0) {
        notify(tWorkorder("error.departmentNoUsers"), "error");
        return;
      }
      fieldEvents.onChange(e);
    },
    [fieldEvents, departments, tWorkorder]
  );

  const handleMachineSelect = useCallback((item: MachineSearch) => {
    setValue("machineId", item.id);
  }, [setValue]);

  if (fetching || fetchingDepartments || !values || departmentsOptions.length === 0) {
    return <Loader size="32" />;
  }

  return (
    <div className={classes.card}>
      <MForm className={classes.form}>
        <div className={classes.field}>
          <label>
            {t("fields.machine")}{" "}
            <span className={classes["required-mark"]}>*</span>
          </label>
          {machineId ? (
            <Text
              className={classes["machine-locked"]}
              disabled
              readOnly
              value={machineDisplay}
              inputClassName={classes["machine-search-input"]}
            />
          ) : (
            <div className={classes["machine-search-wrap"]}>
              <SearchInput<MachineSearch>
                placeholder={t("placeholders.machine")}
                className={classes["machine-search-field"]}
                inputClassName={classes["machine-search-input"]}
                searchUrl={API.machines}
                renderItem={formatMachineSearchLabel}
                renderValue={formatMachineSearchValue}
                searchKeys={["intern", "name", "regNo", "serialNo", "assignedDriverName"]}
                onSelect={handleMachineSelect as any}
                iconPosition="left"
                defaultValue={
                  values.machineId ? String(values.machineId) : undefined
                }
              />
            </div>
          )}
        </div>
        <Select
          key={String(values.departmentId ?? 0)}
          className={classes["input-container"]}
          label={
            <div>
              {t("fields.department")}{" "}
              <span className={classes["required-mark"]}>*</span>
            </div>
          }
          name="departmentId"
          placeholder={t("placeholders.department")}
          options={departmentsOptions}
          icon="keyboard_arrow_down"
          onChange={handleDepartmentChange}
          defaultValue={String(values.departmentId ?? 0)}
          showEmptyOption
          emptyOptionValue={0}
        />
        <Textarea
          className={classes["input-container"]}
          label={t("fields.description")}
          placeholder={t("placeholders.description")}
          name="description"
          defaultValue={String(values.description ?? "")}
          {...fieldEvents}
        />
        <p className={classes["later-note"]}>{t("laterFieldsNote")}</p>
      </MForm>
    </div>
  );
};

export default MainForm;
